import React, { createContext, useContext, useEffect, useState } from "react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const ADMIN_KEY = "habbothek_admin_token";
const USER_KEY = "habbothek_user_token";

const AuthCtx = createContext(null);

export const AuthProvider = ({ children }) => {
  const [adminToken, setAdminToken] = useState(() => localStorage.getItem(ADMIN_KEY));
  const [userToken, setUserToken] = useState(() => localStorage.getItem(USER_KEY));
  const [adminName, setAdminName] = useState(null);
  const [user, setUser] = useState(null);
  const [hasAdmin, setHasAdmin] = useState(true);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const s = await axios.get(`${API}/auth/status`);
        setHasAdmin(s.data.has_admin);
      } catch (e) { /* ignore */ }

      if (adminToken) {
        try {
          const me = await axios.get(`${API}/auth/me`, {
            headers: { Authorization: `Bearer ${adminToken}` },
          });
          setAdminName(me.data.username);
        } catch {
          localStorage.removeItem(ADMIN_KEY);
          setAdminToken(null);
        }
      }
      if (userToken) {
        try {
          const me = await axios.get(`${API}/users/me`, {
            headers: { Authorization: `Bearer ${userToken}` },
          });
          setUser(me.data);
        } catch {
          localStorage.removeItem(USER_KEY);
          setUserToken(null);
        }
      }
      setReady(true);
    })();
    
  }, []);

  // --- Admin ---
  const adminLogin = async (uname, pw) => {
    const form = new URLSearchParams();
    form.append("username", uname);
    form.append("password", pw);
    const { data } = await axios.post(`${API}/auth/login`, form, {
      headers: { "Content-Type": "application/x-www-form-urlencoded" },
    });
    localStorage.setItem(ADMIN_KEY, data.access_token);
    setAdminToken(data.access_token);
    setAdminName(data.username);
    setHasAdmin(true);
  };

  const adminRegister = async (uname, pw) => {
    const { data } = await axios.post(`${API}/auth/register`, { username: uname, password: pw });
    localStorage.setItem(ADMIN_KEY, data.access_token);
    setAdminToken(data.access_token);
    setAdminName(data.username);
    setHasAdmin(true);
  };

  const adminLogout = () => {
    localStorage.removeItem(ADMIN_KEY);
    setAdminToken(null);
    setAdminName(null);
  };

  // --- User ---
  const userLogin = async (habboName, pw) => {
    const form = new URLSearchParams();
    form.append("username", habboName);
    form.append("password", pw);
    const { data } = await axios.post(`${API}/users/login`, form, {
      headers: { "Content-Type": "application/x-www-form-urlencoded" },
    });
    localStorage.setItem(USER_KEY, data.access_token);
    setUserToken(data.access_token);
    await refreshUser(data.access_token);
  };

  const registerStart = async (habboName, hotel, pw) => {
    const { data } = await axios.post(`${API}/users/register/start`, {
      habbo_name: habboName,
      hotel,
      password: pw,
    });
    return data;
  };

  const registerVerify = async (pendingId) => {
    const { data } = await axios.post(`${API}/users/register/verify`, {
      pending_id: pendingId,
    });
    if (data.verified && data.access_token) {
      localStorage.setItem(USER_KEY, data.access_token);
      setUserToken(data.access_token);
      await refreshUser(data.access_token);
    }
    return data;
  };

  const refreshUser = async (tok = userToken) => {
    if (!tok) return;
    const me = await axios.get(`${API}/users/me`, { headers: { Authorization: `Bearer ${tok}` } });
    setUser(me.data);
    return me.data;
  };

  const verifyHabbo = async () => {
    const { data } = await axios.post(`${API}/users/verify-habbo`, {}, {
      headers: { Authorization: `Bearer ${userToken}` },
    });
    if (data.verified) await refreshUser();
    return data;
  };

  const regenerateCode = async () => {
    const { data } = await axios.post(`${API}/users/regenerate-code`, {}, {
      headers: { Authorization: `Bearer ${userToken}` },
    });
    await refreshUser();
    return data;
  };

  const userLogout = () => {
    localStorage.removeItem(USER_KEY);
    setUserToken(null);
    setUser(null);
  };

  return (
    <AuthCtx.Provider
      value={{
        // Admin
        adminToken, adminName, hasAdmin, adminLogin, adminRegister, adminLogout,
        // User
        userToken, user, userLogin, registerStart, registerVerify, userLogout, verifyHabbo, regenerateCode, refreshUser,
        // Common
        ready, API,
      }}
    >
      {children}
    </AuthCtx.Provider>
  );
};

export const useAuth = () => useContext(AuthCtx);
